import { ErrorResponse } from '@citruscamps/citrus-client'
import { faPaperPlane, faRedo } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { default as React } from 'react'
import { useSupport, ISupportRequest } from '../../hooks/useSupport'
import translations from '../../translations/en.json'
import { DashboardLayout } from '../DashboardLayout/DashboardLayout'
import { ErrorContent } from '../ErrorContent/ErrorContent'
import { LoadingButton } from '../LoadingButton/LoadingButton'
import { IErrorViewProps } from './ErrorBoundary'

interface IProps extends IErrorViewProps {
  isBounded?: boolean
}

export const DashboardErrorView = ({ error, errorInfo, isBounded }: IProps) => {
  const summary = error?.toString()
    ? `Issue Reported: "${error.toString()}"`
    : `Dashboard Application Issue Reported`
  const description =
    'There has been an error that has occured.%0D%0A%0D%0A%0D%0A' +
    `${error?.toString()}%0D%0A` +
    `${errorInfo?.componentStack}%0D%0A`
  const { hasSubmitted, isError, isSuccess, handleSubmit } = useSupport()
  const errorContent = {
    code: 500,
    message: (translations as any)['error.default_toast_message'],
    timestamp: new Date().toJSON(),
  }

  if (isBounded && isSuccess && hasSubmitted) {
    return (
      <div className="d-flex" style={{ minHeight: '100vh', paddingBottom: '7.5rem' }}>
        <DashboardSubmittedErrorView error={error} errorContent={errorContent} />
      </div>
    )
  }

  if (isBounded) {
    return (
      <div className="d-flex" style={{ minHeight: '100vh', paddingBottom: '7.5rem' }}>
        <DashboardNotSubmittedErrorView
          summary={summary}
          description={description}
          error={error}
          errorInfo={errorInfo}
          errorContent={errorContent}
          isError={isError}
          onSubmit={handleSubmit}
        />
      </div>
    )
  }

  if (isSuccess && hasSubmitted) {
    return (
      <DashboardLayout>
        <DashboardSubmittedErrorView error={error} errorContent={errorContent} />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <DashboardNotSubmittedErrorView
        summary={summary}
        description={description}
        error={error}
        errorInfo={errorInfo}
        errorContent={errorContent}
        isError={isError}
        onSubmit={handleSubmit}
      />
    </DashboardLayout>
  )
}

type INotSubmittedViewProps = Pick<IProps, 'error' | 'errorInfo'> & {
  summary: string
  description: string
  isError: boolean
  errorContent?: ErrorResponse | null
  onSubmit: (values: ISupportRequest) => Promise<any>
}

export const DashboardNotSubmittedErrorView = ({
  summary,
  description,
  error,
  errorInfo,
  errorContent,
  isError,
  onSubmit: handleSubmit,
}: INotSubmittedViewProps) => (
  <div className="d-flex" style={{ minHeight: '90vh' }}>
    <ErrorContent
      className="my-md-auto w-100 mt-5"
      error={errorContent}
      stack={error?.componentStack || error?.stack}
    >
      <div>
        <LoadingButton
          type="button"
          className="btn btn-outline-primary"
          onClick={async () => {
            await handleSubmit({
              type: 'bug',
              summary,
              description,
              error_stack: errorInfo?.componentStack || error?.stack,
            })
          }}
        >
          <FontAwesomeIcon className="me-2" icon={faPaperPlane} />
          Send report {isError && 'again'}
        </LoadingButton>
      </div>
      <div>
        <button
          type="button"
          className="btn btn-link"
          onClick={() => {
            window.location.reload()
          }}
        >
          <FontAwesomeIcon className="me-2" icon={faRedo} />
          Refresh page
        </button>
      </div>
    </ErrorContent>
  </div>
)

type ISubmittedViewProps = Pick<IProps, 'error'> & {
  errorContent?: ErrorResponse | null
}

export const DashboardSubmittedErrorView = ({ error, errorContent }: ISubmittedViewProps) => (
  <div className="d-flex" style={{ minHeight: '90vh' }}>
    <ErrorContent
      className="my-md-auto w-100 mt-5"
      error={errorContent}
      stack={error?.componentStack || error?.stack}
    >
      <div>Submitted issue to support.</div>
      <div>
        <button
          type="button"
          className="btn btn-link"
          onClick={() => {
            window.location.reload()
          }}
        >
          <FontAwesomeIcon className="me-2" icon={faRedo} />
          Refresh page
        </button>
      </div>
    </ErrorContent>
  </div>
)
